import React from "react";

import Breadcrumbs from "./Breadcrumbs";

const items = [
	{ title: "Uppdrag", path: "/jobs" },
	{ title: "Nytt uppdrag", path: "/jobs/new" },
];

/**
 * Simulates the scroll container each page renders inside — a grey, positioned
 * box. The dashed inner frame is a page that wraps its content (including the
 * breadcrumbs) in padding (`p-4 px-6`). The breadcrumb bar should still sit
 * flush to the very top and side edges of the grey box, never inset by that
 * padding.
 */
const PageFrame: React.FC<{ padded?: boolean; children: React.ReactNode }> = ({
	padded,
	children,
}) => (
	<div className="relative h-40 overflow-hidden rounded-md bg-[#f2f2f2]">
		<div className={padded ? "p-4 px-6" : ""}>
			<div className={padded ? "border border-dashed border-blue-300" : ""}>{children}</div>
		</div>
	</div>
);

/**
 * Example for {@link Breadcrumbs} — the top-of-page navigation bar (page title +
 * "/ sub page", optional status pill / pool dropdown). It is `fullBleed` by
 * default: it measures its parent's padding and cancels it with negative
 * margins, so it renders identically whether or not the page wraps it in a
 * padded content container.
 */
// NOTE: no router wrapper — the showcase page already renders inside the app's
// BrowserRouter, and react-router v6 throws on nested routers.
const BreadcrumbsExample: React.FC = () => {
	return (
		<div className="max-w-3xl space-y-8 p-6">
			<div>
				<h2 className="mb-2 text-xl font-bold">Breadcrumbs</h2>
				<p className="text-sm text-gray-600">
					Top-of-page nav bar. <code>fullBleed</code> (default) keeps it flush to the top/side edges
					regardless of parent padding — compare the two frames below; the bar looks the same in
					both.
				</p>
			</div>

			<div className="space-y-2">
				<p className="text-xs font-semibold uppercase text-gray-500">
					Direct child (no parent padding)
				</p>
				<PageFrame>
					<Breadcrumbs items={items} />
				</PageFrame>
			</div>

			<div className="space-y-2">
				<p className="text-xs font-semibold uppercase text-gray-500">
					Inside a padded page (<code>p-4 px-6</code>) — still flush thanks to fullBleed
				</p>
				<PageFrame padded>
					<Breadcrumbs items={items} />
				</PageFrame>
			</div>

			<div className="space-y-2">
				<p className="text-xs font-semibold uppercase text-gray-500">
					<code>fullBleed=false</code> — flows inline with the parent padding
				</p>
				<PageFrame padded>
					<Breadcrumbs items={items} fullBleed={false} />
				</PageFrame>
			</div>
		</div>
	);
};

export default BreadcrumbsExample;
